import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceDot,
} from "recharts";
import { DASH } from "@/constants/testIds";

const TARIFF_SHIFT_DATE = "2025-07"; // TNB tariff revision Jul 2025

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E5E5DE] p-3 font-mono-data text-xs">
      <div className="text-[#1A1B1A] font-semibold mb-2">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex justify-between gap-6" style={{ color: p.color }}>
          <span>{p.name}</span>
          <span>{typeof p.value === "number" ? p.value.toFixed(3) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function LoadFactorChart({ months, spikes = [] }) {
  const data = months.map((m) => ({
    month: m.month_label,
    period: m.period_start?.slice(0, 7),
    load_factor: m.load_factor,
    cost_per_kwh: m.cost_per_kwh,
  }));

  // Tariff shift marker — only show if shift date falls WITHIN the data window
  const firstPeriod = data[0]?.period || "";
  const lastPeriod = data[data.length - 1]?.period || "";
  const shiftInRange = firstPeriod && lastPeriod && TARIFF_SHIFT_DATE >= firstPeriod && TARIFF_SHIFT_DATE <= lastPeriod;
  const tariffIdx = shiftInRange ? data.findIndex((d) => (d.period || "") >= TARIFF_SHIFT_DATE) : -1;
  const tariffMonth = tariffIdx >= 0 ? data[tariffIdx]?.month : null;

  const spikeMonths = new Set((spikes || []).map((s) => s.month_label));

  return (
    <div
      data-testid={DASH.loadfactorChart}
      className="flat-card p-6 sm:p-8"
    >
      <div className="flex items-start justify-between mb-6 flex-wrap gap-2">
        <div>
          <div className="overline mb-2">Chart · Featured</div>
          <h3 className="font-display text-2xl tracking-tight">Load Factor over Cost per kWh</h3>
          <p className="text-sm text-[#6E716C] mt-1 max-w-md">
            Bars — cost intensity (RM/kWh). Line — load factor (0–1, higher = flatter usage vs. your own peak).
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs font-mono-data flex-wrap">
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 bg-[#B24F35]"></span> Cost/kWh
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-[2px] bg-[#0F3524]"></span> Load factor
          </span>
          {tariffMonth && (
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 border border-dashed border-[#6E716C]"></span> Tariff shift
            </span>
          )}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart data={data} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid stroke="#EDEDE8" strokeDasharray="0" vertical={false} />
          <XAxis dataKey="month" tickLine={false} axisLine={false} />
          <YAxis
            yAxisId="left"
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `RM ${v.toFixed(2)}`}
            width={80}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tickLine={false}
            axisLine={false}
            domain={[0, 1]}
            tickFormatter={(v) => v.toFixed(2)}
            width={50}
          />
          <Tooltip content={<TooltipBox />} cursor={{ fill: "#F0F0EA" }} />
          {tariffMonth && (
            <ReferenceLine
              yAxisId="left"
              x={tariffMonth}
              stroke="#6E716C"
              strokeDasharray="4 4"
              label={{ value: "Tariff Jul 2025", position: "insideTopRight", fill: "#6E716C", fontSize: 10, fontFamily: "JetBrains Mono" }}
            />
          )}
          {data.map((d, i) =>
            spikeMonths.has(d.month) ? (
              <ReferenceDot
                key={`spike-${i}`}
                yAxisId="left"
                x={d.month}
                y={d.cost_per_kwh}
                r={6}
                stroke="#B24F35"
                strokeWidth={2}
                fill="#F7F7F4"
              />
            ) : null
          )}
          <Bar
            yAxisId="left"
            dataKey="cost_per_kwh"
            name="Cost/kWh"
            fill="#B24F35"
            barSize={28}
            isAnimationActive
          />
          <Line
            yAxisId="right"
            type="linear"
            dataKey="load_factor"
            name="Load factor"
            stroke="#0F3524"
            strokeWidth={2}
            dot={{ r: 4, fill: "#0F3524" }}
            isAnimationActive
            animationDuration={1500}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
